import Handlebars from 'handlebars';
import Datepicker from '../inputs/datepicker';
import MessageHelper from '../../helpers/message-helper';

export default class TireTable {

  private readonly rows: JQuery;
  private readonly rowTemplate: any;
  private readonly newTireRow: JQuery;
  private readonly tireInput: JQuery;
  private readonly errorMessage: JQuery;
  private readonly newTireErrorMessageSelector: string = '.js-newTireErrorMessage';
  private readonly newTireInputSelector: string = '.js-newTireInput';
  private readonly kmDuringDisassembly: JQuery = $('body').find('.js-kmDuringDisassembly');
  private readonly kmDuringAssembly: string = '.js-kmDuringAssembly';
  private readonly removeAllPositions: JQuery = $('body').find('.js-removeAllPositions');

  private static readonly ASSEMBLY_DATE_SELECTOR = '.js-assemblyDate';
  private static readonly POSITIONS_SELECTOR: string = '.js-positions';

  constructor(
    private readonly container: JQuery,
    private readonly disassemblyDate: JQuery,
  ) {
    this.rows = container.find('.js-tires');
    this.rowTemplate = Handlebars.compile($('#js-tireRowTemplate').html());
    this.newTireRow = container.find('.js-addNewTireRow');
    this.tireInput = container.find('.js-addNewTire');
    this.errorMessage = container.find('.js-errorMessage');

    this.bindAddNewTire();
    this.bindRemoveTire();
    this.bindKmAssembly();
    this.bindRemoveAllPositions();
    this.bindAssemblyDateChange();
    this.bindValidateNewTire();

    this.initAssemblyDate();

    this.bindPositionChange();
    this.disableSelectedOptions();
  }

  private initAssemblyDate() {
    const setMaxDate = (assemblyDate: JQuery, maxDate: Date) => {
      if (maxDate < assemblyDate.datepicker('getDate')) {
        assemblyDate.parent().siblings('.js-inputNote').html(MessageHelper.getMessage('eparts.validation.adjustedDate'));
      }
      assemblyDate.datepicker('option', { maxDate });
    };

    const setAllMaxDates = () => {
      const maxDate = this.getMaxDate();
      this.rows.find(TireTable.ASSEMBLY_DATE_SELECTOR).each((i, el): void => setMaxDate($(el), maxDate));
    };

    this.disassemblyDate.on('change', setAllMaxDates);
    setAllMaxDates();
  }

  private bindAddNewTire() {
    const addRowForValue = (e) => {
      const value = $(e.currentTarget).val() as string;
      if (value.length) {
        $(e.target).off();
        this.errorMessage.empty();
        this.addTireRow(value);
      }
    };

    this.tireInput.on('blur ', addRowForValue);
    this.tireInput.on('keyup', (e) => {
      const key = e.key || e.keyCode;
      if (key === 'Enter' || key === 13) {
        addRowForValue(e);
      }
    });
  }

  private bindValidateNewTire() {

    $(this.newTireInputSelector).on('blur', (e) => {
      const materialNumber = $(e.currentTarget).val();
      this.validateNewTire(e, materialNumber);
    });
    $(this.newTireInputSelector).on('keyup', (e) => {
      const materialNumber = $(e.currentTarget).val();
      const key = e.key || e.keyCode;
      if (key === 'Enter' || key === 13) {
        this.validateNewTire(e, materialNumber);
      }
    });

  }

  private bindRemoveTire() {
    this.rows.on('click', '.js-deleteTireRow', (e) => {
      const row = $(e.target).closest('.js-tireRow');
      const entryIndex = row.data('index') as number;
      const errorRow = this.rows.find(`.js-errorRow[data-index=${entryIndex}]`);
      row.remove();
      errorRow.remove();
      this.handleRowChange();
    });
  }

  private bindKmAssembly() {
    this.kmDuringDisassembly.on('change', () => {
      $(this.kmDuringAssembly).each((e, t) => {
        $(t)
          .parsley()
          .validate();
      });
    });
  }

  private getTire(materialNumber: string) {
    return $.ajax({
      url: `${window.ACC.config.encodedContextPath}/tireWarranty/tire?materialNumber=${encodeURIComponent(materialNumber)}`,
      type: 'GET',
      dataType: 'json',
      contentType: 'application/json',
      data: {
        tireMaterialNumber: materialNumber,
      },
    });
  }

  addTireRow(materialNumber) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.getTire(materialNumber)
      .done((tireData) => {
        const tireBrand = this.getBrandFromTireData(tireData);
        const index = this.getNumberOfRows();
        // eslint-disable-next-line no-param-reassign
        materialNumber = tireData.code;
        const newRow = $(this.rowTemplate({ index, materialNumber, tireBrand }));
        newRow.insertBefore(this.newTireRow);
        newRow.find('.js-focus').focus();

        this.handleRowChange();

        const maxDate = this.getMaxDate();
        Datepicker.init(newRow);
        newRow.find(TireTable.ASSEMBLY_DATE_SELECTOR).datepicker('option', { maxDate });
        this.disableSelectedOptions();
      })
      .fail((error) => {
        this.errorMessage.html(error.responseJSON);
        this.tireInput.focus();
      })
      .always(() => {
        this.tireInput.val('');
        this.bindAddNewTire();
        this.bindValidateNewTire();
      });
  }

  validateNewTire(e, materialNumber) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.getTire(materialNumber)
      .done((tireData) => {
        $(e.currentTarget).siblings(this.newTireErrorMessageSelector).html('');
      })
      .fail((error) => {
        $(e.currentTarget).siblings(this.newTireErrorMessageSelector).html(error.responseJSON);
      }).always(() => {
        $(e.currentTarget).change();
      });
  }

  private getBrandFromTireData(tireData): string {
    try {
      const tireClassification = tireData.classifications.find((classification) => classification.code === 'tire');
      const brandClassificationFeature = tireClassification.features.find((feature) => feature.code === 'B2BepartsClassification/1.0/tire.tirebrand');
      return brandClassificationFeature.featureValues[0].value as string;
    } catch {
      console.error('Invalid format on TireData');
      return '';
    }
  }

  private getNumberOfRows(): number {
    return this.rows.find('.js-tireRow').length;
  }

  private handleRowChange() {
    this.setMandatoryColumns();
    this.checkMaxRows();
    this.reindexColumnFieldNames();
    this.tireInput.change();
    this.disableSelectedOptions();
  }

  private setMandatoryColumns() {
    const isMandatory = this.getNumberOfRows() > 1;

    this.rows.find('.js-damageDescription').prop('required', isMandatory);
    this.rows.find('.js-damageDescription').each((e, t) => {
      $(t)
        .parsley()
        .validate();
    });
  }

  private checkMaxRows() {
    if (this.getNumberOfRows() >= 4) {
      this.tireInput.prop('disabled', true);
      this.newTireRow.hide();
    } else {
      this.tireInput.prop('disabled', false);
      this.newTireRow.show();
    }
  }

  private reindexColumnFieldNames() {
    this.rows.children().each((i, row) => {
      $(row).find('[name]').each((j, input) => {
        const $input = $(input);
        const oldName = $input.prop('name');
        $input.prop('name', oldName.replace(/\[[0-9]+\]/, `[${i}]`));
      });
    });
  }

  private bindRemoveAllPositions(): void {
    if (!this.removeAllPositions.prop('disabled')) {
      this.removeAllPositions.on('click', () => {
        this.container.find('.js-tireRow').remove();
        this.checkMaxRows();
      });
    }
  }

  private bindAssemblyDateChange(): void {
    this.container.on('change', TireTable.ASSEMBLY_DATE_SELECTOR, (e): void => {
      $(e.currentTarget).parent().siblings('.js-inputNote').html('');
    });
  }

  private bindPositionChange(): void {
    this.container.on('change', TireTable.POSITIONS_SELECTOR, (): void => {
      this.disableSelectedOptions();
    });
  }

  private disableSelectedOptions(): void {
    this.container.find(TireTable.POSITIONS_SELECTOR)
      .find('option')
      .filter((i, el) => $(el).val() !== '')
      .prop('disabled', false);
    this.container.find(TireTable.POSITIONS_SELECTOR).each((i, el): void => {
      this.container.find(TireTable.POSITIONS_SELECTOR).not($(el)).find(`option[value="${$(el).val() as number | string}"]`).prop('disabled', true);
    });
  }

  private getMaxDate(): Date {
    let maxDate = this.disassemblyDate.datepicker('getDate') as Date;
    if (!maxDate) {
      maxDate = new Date();
    }
    maxDate.setDate(maxDate.getDate() - 1);
    return maxDate;
  }

}
